// Writing days are local calendar days, keyed 'YYYY-MM-DD'. A writer who
// works past midnight can move the boundary later ("my day ends at 4 a.m."),
// so the key for a moment is the date of that moment minus the boundary.

const pad = (n: number) => String(n).padStart(2, '0')

function keyOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Noon avoids daylight-saving edges when stepping whole days. */
function dateOf(key: string): Date {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(year, month - 1, day, 12)
}

/** The writing day a moment belongs to. */
export function writingDayKey(moment: Date | number, dayStartHour = 0): string {
  const shifted = new Date(typeof moment === 'number' ? moment : moment.getTime())
  shifted.setHours(shifted.getHours() - dayStartHour)
  return keyOf(shifted)
}

export function addDaysToKey(key: string, days: number): string {
  const date = dateOf(key)
  date.setDate(date.getDate() + days)
  return keyOf(date)
}

export function dayOfWeek(key: string): number {
  return dateOf(key).getDay()
}

/** The first day of the week containing `key`, weeks starting on `weekStartsOn` (0 = Sunday). */
export function weekStartKey(key: string, weekStartsOn: number): string {
  return addDaysToKey(key, -((dayOfWeek(key) - weekStartsOn + 7) % 7))
}

export function daysBetween(fromKey: string, toKey: string): number {
  return Math.round((dateOf(toKey).getTime() - dateOf(fromKey).getTime()) / 86_400_000)
}

export interface StreakInput {
  today: string
  qualifies: (key: string) => boolean
  daysPerWeek: number
  weekStartsOn: number
  /** How far back to look; a streak longer than this is reported at the cap. */
  maxWeeks?: number
}

export interface StreakResult {
  /** Calendar days in the streak, rest days included. 0 when there is none. */
  days: number
  /** The first writing day of the streak, or null. */
  startedOn: string | null
  /** Qualifying days so far this week, today included. */
  daysThisWeek: number
}

/**
 * A streak is a run of weeks that each met the days-per-week quota. Every
 * calendar day inside the run counts, so planned rest days never break it.
 * A streak can begin on any day: its first week only needs the days that
 * were left in it (starting on a Sunday, one day covers that week). The
 * current week keeps a run alive while its full quota is still reachable;
 * once it is not, the run ends, though a new streak can begin that week.
 */
export function computeStreak({ today, qualifies, daysPerWeek, weekStartsOn, maxWeeks = 520 }: StreakInput): StreakResult {
  const target = Math.min(7, Math.max(1, Math.round(daysPerWeek)))
  const thisWeek = weekStartKey(today, weekStartsOn)
  /** The quota for a week whose streak began on `first`. */
  const startingQuota = (weekStart: string, first: string) =>
    Math.min(target, 7 - daysBetween(weekStart, first))

  let daysThisWeek = 0
  let firstThisWeek: string | null = null
  for (let key = thisWeek; key <= today; key = addDaysToKey(key, 1)) {
    if (qualifies(key)) {
      daysThisWeek++
      firstThisWeek ??= key
    }
  }
  const daysLeft = 6 - daysBetween(thisWeek, today) + (qualifies(today) ? 0 : 1)

  // Walk back through completed weeks while each met its quota. The earliest
  // week of the run may be the partial week in which the streak began.
  let chainStart: string | null = null
  for (let week = 1; week <= maxWeeks; week++) {
    const start = addDaysToKey(thisWeek, -7 * week)
    let count = 0
    let first: string | null = null
    for (let day = 0; day < 7; day++) {
      const key = addDaysToKey(start, day)
      if (qualifies(key)) {
        count++
        first ??= key
      }
    }
    if (count >= target) {
      chainStart = first
      continue
    }
    if (first && count >= startingQuota(start, first)) chainStart = first
    break
  }

  const streakFrom = (startedOn: string | null): StreakResult => ({
    days: startedOn ? daysBetween(startedOn, today) + 1 : 0,
    startedOn,
    daysThisWeek,
  })

  // Continuing a run needs the full quota this week.
  if (chainStart && daysThisWeek + daysLeft >= target) return streakFrom(chainStart)

  // Otherwise a streak may begin on any day this week, needing only the days
  // left from that day. The earliest workable start gives the longest streak.
  let countFrom = daysThisWeek
  for (let key = thisWeek; key <= today; key = addDaysToKey(key, 1)) {
    if (!qualifies(key)) continue
    if (countFrom + daysLeft >= startingQuota(thisWeek, key)) return streakFrom(key)
    countFrom--
  }
  return streakFrom(null)
}
